import { useCallback, useMemo } from 'react';
import { analytics } from 'lib/analytics';
import { subscriptionValue, type SubscriptionInput } from 'lib/analytics/subscription-value';
import { isLastCancelableSubscription } from 'lib/billing';
import { useLeaveDialog } from 'features/cancellation/leaveDialogStore';
import { findStoredSubscription, useBillingStore } from '../store/useBillingStore';
import { useSubscriptionAction, type SubscriptionAction } from './useSubscriptionAction';

type Params = {
  subscriptionId?: string | null;
  subscriptionItemId?: string | null;
  /**
   * The subscription being acted on, as the billing store holds it. Only read
   * for analytics — seats, amount, period, products — so the person who cancels
   * can be ranked by what they paid. Optional: analytics never blocks a cancel.
   */
  subscription?: SubscriptionInput | null;
  successMessage: string;
  errorMessage: string;
  /** For a page that keeps its own copy of the subscription outside the billing store. */
  onCancelled?: () => void;
};

/**
 * With `subscriptionItemId` this removes one line; without it, it cancels the whole
 * subscription. The caller picks based on line count — the backend rejects removing the last.
 *
 * Cancellation intent is tracked here rather than at each call site: this hook is the
 * single funnel for every cancel in checkout-v2, and the database cannot record intent
 * at all — `ends_on` is cleared on resume, so an abandoned or reversed cancellation
 * leaves no trace once the row is updated.
 *
 * A cancel that ends the customer's last running plan opens the cancellation dialog instead of
 * the caller's confirm, so the exit survey sees everyone who leaves, whichever screen they used.
 */
export function useCancelSubscription({
  subscriptionId,
  subscriptionItemId,
  subscription,
  successMessage,
  errorMessage,
  onCancelled
}: Params): SubscriptionAction {
  const value = useMemo(() => subscriptionValue(subscription), [subscription]);
  const isPartial = Boolean(subscriptionItemId);
  // Every screen that cancels keeps the billing store loaded, so it holds every subscription.
  const subscriptions = useBillingStore((state) => state.subscriptions);
  const openLeaveDialog = useLeaveDialog((state) => state.open);
  const endingIds = useLeaveDialog((state) => state.endingIds);
  const markEnding = useLeaveDialog((state) => state.markEnding);
  // Removing one line always leaves the rest of the subscription running.
  const leaving = !isPartial && isLastCancelableSubscription(subscriptions, subscriptionId, endingIds);

  const perform = useCallback(
    async (client: any, userId: string) => {
      const res = await (subscriptionItemId
        ? client.removeSubscriptionItem({ userId, subscriptionId, subscriptionItemId })
        : client.cancelSubscription({ userId, subscriptionId }));
      // Success only. The snapshot counts the row too, but only this knows the
      // customer confirmed it — server-side, self-serve and staff are one path.
      analytics.subscriptionCanceled({ ...value, is_partial: isPartial });
      // A whole cancel puts the plan in its cancellation period at once, whatever billing reports yet.
      if (!isPartial && subscriptionId) markEnding([subscriptionId]);
      onCancelled?.();
      return res;
    },
    [subscriptionId, subscriptionItemId, value, isPartial, onCancelled, markEnding]
  );

  const action = useSubscriptionAction({ ready: Boolean(subscriptionId), perform, successMessage, errorMessage });

  return useMemo(
    () => ({
      ...action,
      openModal: () => {
        const stored = leaving && findStoredSubscription(subscriptions, subscriptionId);
        // The dialog tracks its own intent and outcome.
        if (stored) {
          openLeaveDialog({ subscriptions: [stored], onCancelled });
          return;
        }
        analytics.cancelIntended({ ...value, is_partial: isPartial });
        action.openModal();
      },
      // `run` closes the modal itself on success, so reaching close() while it is
      // open means the customer backed out — the save this whole event exists for.
      close: () => {
        if (action.open && !action.busy) {
          analytics.cancelAborted({ ...value, is_partial: isPartial });
        }
        action.close();
      }
    }),
    [action, value, isPartial, leaving, subscriptions, subscriptionId, openLeaveDialog, onCancelled]
  );
}

/** Clears a scheduled cancellation. Only possible while the period is still running. */
export function useResumeSubscription({
  subscriptionId,
  subscription,
  successMessage,
  errorMessage
}: Omit<Params, 'subscriptionItemId'>): SubscriptionAction {
  const value = useMemo(() => subscriptionValue(subscription), [subscription]);
  const unmarkEnding = useLeaveDialog((state) => state.unmarkEnding);

  const perform = useCallback(
    async (client: any, userId: string) => {
      const res = await client.resumeSubscription({ userId, subscriptionId });
      // Fired on success only. The subscription row loses `ends_on` a moment from
      // now, taking with it the only evidence the cancellation ever happened.
      analytics.subscriptionResumed(value);
      if (subscriptionId) unmarkEnding(subscriptionId);
      return res;
    },
    [subscriptionId, value, unmarkEnding]
  );

  return useSubscriptionAction({ ready: Boolean(subscriptionId), perform, successMessage, errorMessage });
}
